<template>
    <div>
        <loader v-if="isLoading" />
        <!-- display lab result -->
        <div v-else>
            <!-- search input -->
            <div class="row col-12 col-md-6 mb-3">
                <input
                    type="search"
                    v-model="searchInput"
                    class="form-control"
                    placeholder="Search by test name, result, or comment..."
                />
            </div>
            <div class="table-responsive">
                <table
                    class="table b-table list table-hover table-borderless preview-result-table"
                >
                    <template v-for="(test, index) in filteredTestResult">
                        <thead :key="test.name + index">
                            <tr class="test-name">
                                <th
                                    colspan="4"
                                    style="background-color: #c9ccde;"
                                >
                                    {{ test.name }}
                                    <div
                                        v-if="test.message"
                                        class="font-weight-normal"
                                    >
                                        {{ test.message }}
                                    </div>
                                </th>
                            </tr>
                            <tr>
                                <th width="20%">Test:</th>
                                <th>Result:</th>
                                <th class="text-nowrap">Reference Range:</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody
                            v-for="(item, iindex) in test.data"
                            :key="item.name + iindex"
                        >
                            <tr
                                :class="{
                                    'border-bottom-0': item.comment,
                                    selected: isSelected(index, iindex)
                                }"
                                @click="selectRow(index, iindex, item)"
                            >
                                <td class="cursor-pointer text-link">
                                    <b v-html="item.name"></b>
                                </td>
                                <td
                                    :class="setAbaxisAbnormalCode(item)"
                                    v-html="item.result"
                                ></td>
                                <td
                                    class="cursor-pointer"
                                    v-html="item.reference_range"
                                ></td>
                                <td>
                                    <lab-reference-range
                                        v-if="
                                            item &&
                                            item.show_bar &&
                                            validResult(item.result)
                                        "
                                        :percentage="item.pct"
                                        :show-bar="item.show_bar"
                                        :range="item.reference_range"
                                    />
                                </td>
                            </tr>
                            <tr
                                v-if="item.comment"
                                :class="{
                                    'border-top-0 cursor-pointer': item.comment,
                                    selected: isSelected(index, iindex)
                                }"
                                @click="selectRow(index, iindex, item)"
                            >
                                <td></td>
                                <td
                                    colspan="3"
                                    class="cursor-pointer"
                                    v-html="decodeHtml(item.comment)"
                                ></td>
                            </tr>
                        </tbody>
                    </template>
                </table>
            </div>
        </div>
    </div>
</template>
<script>
import PatientLabService from "@/services/patient/patient-lab";
import Loader from "@/components/elements/loader-list";
import { has, debounce } from "lodash";
import EXTENSIONS from "@/constants/extensions";
import LabReferenceRange from "@/components/commons/lab-reference-range";

export default {
    components: {
        Loader,
        LabReferenceRange
    },
    props: {
        labId: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            isLoading: false,
            testResult: null,
            searchInput: "",
            searchQuery: "",
            selectedRow: {
                itemIndex: 0,
                testIndex: 0
            }
        };
    },
    created() {
        this.debouncedSearch = debounce(this.updateSearchQuery, 300);
    },
    mounted() {
        this.init();
    },
    watch: {
        searchInput: {
            handler(newValue) {
                this.debouncedSearch(newValue);
            }
        }
    },
    computed: {
        filteredTestResult() {
            if (!this.testResult || !this.searchQuery) {
                return this.testResult;
            }

            const query = this.searchQuery.toLowerCase();

            return this.testResult
                .map(test => {
                    // Skip if test is invalid
                    if (!test || !test.data) {
                        return null;
                    }

                    // If test name matches, include all data
                    if (test.name && test.name.toLowerCase().includes(query)) {
                        return test;
                    }

                    // Filter data array for matching items
                    const filteredData = test.data.filter(item => {
                        return (
                            item &&
                            ((item.name &&
                                item.name.toLowerCase().includes(query)) ||
                                (item.result &&
                                    item.result
                                        .toString()
                                        .toLowerCase()
                                        .includes(query)) ||
                                (item.comment &&
                                    item.comment.toLowerCase().includes(query)))
                        );
                    });

                    // Only include test if it has matching data items
                    if (filteredData.length > 0) {
                        return {
                            ...test,
                            data: filteredData
                        };
                    }

                    return null;
                })
                .filter(Boolean); // Remove null entries
        }
    },
    beforeDestroy() {
        this.debouncedSearch.cancel();
    },
    methods: {
        updateSearchQuery(value) {
            this.searchQuery = value;
        },
        async init() {
            this.isLoading = true;
            let self = this;
            PatientLabService.getLabTestResult(this.labId)
                .then(result => {
                    this.isLoading = false;
                    this.$emit("loaded", true);
                    if (result.code == 200) {
                        if (result.resource.length > 0) {
                            this.$emit("has-data", true);
                            self.testResult = result.resource;
                            //get the first result
                            let firstResult =
                                self.testResult[0]?.data?.[0] ?? null;
                            firstResult && this.getLabHistory(firstResult);
                        }
                    } else {
                        this._message("No results found", "danger");
                    }
                })
                .catch(err => {
                    this._message(err, "danger");
                });
        },
        decodeHtml(html) {
            var txt = document.createElement("textarea");
            txt.innerHTML = html;
            return txt.value;
        },
        getLabHistory(data, onClick) {
            this.$emit("reload-lab-history", {
                labId: this.labId,
                sample_id: data.resultLabId,
                code: data.code,
                name: data.name,
                onClick: onClick
            });
        },
        setAbaxisAbnormalCode(data) {
            if (
                has(data, "abnormal") &&
                data.abnormal &&
                data.source == EXTENSIONS.CODE.CODE_ABAXIS_FUSE
            ) {
                return "text-danger";
            }
        },
        selectRow(testIndex, itemIndex, item) {
            this.selectedRow = { testIndex, itemIndex };
            this.getLabHistory(item, true);
        },
        isSelected(testIndex, itemIndex) {
            return (
                this.selectedRow &&
                this.selectedRow.testIndex === testIndex &&
                this.selectedRow.itemIndex === itemIndex
            );
        },
        validResult(value) {
            return !isNaN(parseFloat(value));
        }
    }
};
</script>
<style type="text/css" lang="scss">
.preview-result-table {
    border-bottom: 1px solid #ddd;
    margin-bottom: 0px;

    .test-name {
        font-size: 15px;
    }

    tr.border-bottom-0 {
        border-bottom: 0px;

        td {
            border-bottom: 0px;
        }
    }

    tr.border-top-0 {
        border-top: 0px;

        td {
            border-top: 0px;
        }
    }
}
</style>

<style scoped>
.text-link {
    font-weight: normal;
}

.selected {
    color: #212529;
    background-color: rgba(0, 0, 0, 0.095);
}

.table-responsive {
    width: 100%;
    overflow-x: auto;
}

.table {
    width: 100%;
    table-layout: auto; /* Adjusts column width based on content */
}
</style>
